/**
 * API client for the Enereg backend.
 * All data fetching goes through these functions.
 * Supports JWT auth (Supabase Auth) and session cookie auth (Bun server).
 */

const BASE = "/api";

// Token storage — in memory + sessionStorage (tab-scoped, cleared on close)
// Uses sessionStorage instead of localStorage to reduce XSS token theft window
let _accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  _accessToken = token;
  if (token) {
    sessionStorage.setItem("enereg_token", token);
  } else {
    sessionStorage.removeItem("enereg_token");
  }
  // Clean up any legacy localStorage tokens
  try { localStorage.removeItem("enereg_token"); } catch {}
}

export function getAccessToken(): string | null {
  if (!_accessToken) {
    _accessToken = sessionStorage.getItem("enereg_token");
  }
  return _accessToken;
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${url}`, {
    headers,
    ...options,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `API error: ${res.status}`);
  }
  return res.json();
}

// Auth
export const auth = {
  signUp: (data: { email: string; password: string; name: string; org_name?: string; beta_code?: string }) =>
    fetchJSON<{ user: any }>("/auth/signup", { method: "POST", body: JSON.stringify(data) }),

  logIn: (data: { email: string; password: string }) =>
    fetchJSON<{ user: any; access_token?: string; refresh_token?: string }>("/auth/login", { method: "POST", body: JSON.stringify(data) }),

  logOut: () => fetchJSON<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  getMe: () => fetchJSON<{ user: any | null; beta_required?: boolean }>("/auth/me"),

  verifyBeta: (code: string) =>
    fetchJSON<{ ok: boolean }>("/auth/verify-beta", { method: "POST", body: JSON.stringify({ beta_code: code }) }),
};

// Projects
export const api = {
  getProjects: () => fetchJSON<any[]>("/projects"),

  getProject: (id: string) => fetchJSON<any>(`/projects/${id}`),

  renameProject: (id: string, name: string) =>
    fetchJSON<{ ok: boolean; name: string }>(`/projects/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),

  deleteProject: (id: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${id}`, { method: "DELETE" }),

  createProject: (data: { name: string; country_code: string; capacity_kw: number; energy_type?: string }) =>
    fetchJSON<{ projectId: string; licensesCreated: number }>("/projects", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getProjectFields: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/fields`),

  updateProjectField: (projectId: string, fieldId: string, value: string | null) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/fields`, {
      method: "PATCH",
      body: JSON.stringify({ field_id: fieldId, value }),
    }),

  getProjectLicenses: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/licenses`),

  getProjectDocuments: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/documents`),

  getProjectAlerts: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/alerts`),

  getLicenseTypes: (country: string) => fetchJSON<any[]>(`/license-types/${country}`),

  getFieldDefinitions: (country: string) => fetchJSON<any[]>(`/field-definitions/${country}`),

  // Documents & Extraction
  uploadDocument: async (projectId: string, file: File, docType: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    const res = await fetch(`${BASE}/projects/${projectId}/documents`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error.error || `Upload failed: ${res.status}`);
    }
    return res.json();
  },

  extractDocument: (projectId: string, docId: string) =>
    fetchJSON<any>(`/projects/${projectId}/documents/${docId}/extract`),

  deleteDocument: (projectId: string, docId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/documents/${docId}`, { method: "DELETE" }),

  retryExtraction: (projectId: string, docId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/documents/${docId}/retry`, { method: "POST" }),

  recordExtractionFeedback: (projectId: string, feedback: {
    document_id: string;
    field_id: string;
    extracted_value?: string;
    extracted_confidence?: number;
    action: "confirmed" | "corrected" | "rejected";
    corrected_value?: string;
    doc_type?: string;
    country_code?: string;
  }) => fetchJSON<{ ok: boolean; id: string }>(`/projects/${projectId}/extraction-feedback`, {
    method: "POST",
    body: JSON.stringify(feedback),
  }),

  getProjectCosts: (projectId: string) =>
    fetchJSON<{ total_usd: number; by_operation: Record<string, number>; details: any[] }>(`/projects/${projectId}/costs`),

  askRegulatoryQuestion: (question: string, countryCode: string, context?: string) =>
    fetchJSON<{ answer: string; sources: any[]; is_mock: boolean }>("/regulatory-qa", {
      method: "POST",
      body: JSON.stringify({ question, country_code: countryCode, context }),
    }),

  classifyDocument: (projectId: string, docId: string) =>
    fetchJSON<{ docType: string | null; confidence: number }>(`/projects/${projectId}/documents/${docId}/classify`),

  updateDocumentType: (projectId: string, docId: string, docType: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/documents/${docId}/type`, {
      method: "PATCH",
      body: JSON.stringify({ doc_type: docType }),
    }),

  batchUpdateFields: (projectId: string, updates: Array<{ field_id: string; value: string; source_document_id?: string; source_page?: number }>) =>
    fetchJSON<{ ok: boolean; updated: number }>(`/projects/${projectId}/fields/batch`, {
      method: "POST",
      body: JSON.stringify({ updates }),
    }),

  getDocumentFileUrl: (projectId: string, docId: string) =>
    `${BASE}/projects/${projectId}/documents/${docId}/file`,

  getDocumentFields: (projectId: string, docId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/documents/${docId}/fields`),

  getDocTypes: () => fetchJSON<any>(`/doc-types`),

  // Templates
  getProjectTemplates: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/templates`),

  getTemplateCompleteness: (projectId: string, templateId: string) =>
    fetchJSON<any>(`/projects/${projectId}/templates/${templateId}`),

  generateTemplate: async (projectId: string, templateId: string) => {
    // Kick off background generation
    const start = await fetchJSON<any>(`/projects/${projectId}/templates/${templateId}/generate`, { method: "POST" });
    if (!start?.generation_id) throw new Error("Failed to start generation");

    // Poll for completion
    const generationId = start.generation_id;
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 2000));
      const status = await fetchJSON<any>(
        `/projects/${projectId}/templates/${templateId}/generate/status?id=${generationId}`
      );
      if (status.status === "complete") return status;
      if (status.status === "error") throw new Error(status.error || "Generation failed");
    }
    throw new Error("Generation timed out");
  },

  downloadTemplate: async (projectId: string, templateId: string) => {
    const res = await fetch(`${BASE}/projects/${projectId}/templates/${templateId}/download`, { method: "POST" });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const filename = res.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] || "template.docx";
    // Trigger browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return { filename };
  },

  getProjectActivity: (projectId: string) => fetchJSON<any[]>(`/projects/${projectId}/activity`),

  getActivityStreak: (projectId: string) =>
    fetchJSON<{ fields_updated_today: number }>(`/projects/${projectId}/activity-streak`),

  // Evaluation
  getProjectEvaluation: (projectId: string) => fetchJSON<any>(`/projects/${projectId}/evaluation`),

  // Alerts
  dismissAlert: (projectId: string, alertId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/alerts/${alertId}/dismiss`, { method: "POST" }),

  // Compliance
  getProjectCompliance: (projectId: string) =>
    fetchJSON<{ items: any[]; summary: any }>(`/projects/${projectId}/compliance`),

  updateProjectCompliance: (projectId: string, countryComplianceId: string, status: string, evidenceNotes?: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/compliance`, {
      method: "PATCH",
      body: JSON.stringify({ country_compliance_id: countryComplianceId, status, evidence_notes: evidenceNotes }),
    }),

  getCountryCompliance: (country: string, opts?: { pillar?: "ENV" | "SOC"; track?: "government" | "investment" }) => {
    const params = new URLSearchParams();
    if (opts?.pillar) params.set("pillar", opts.pillar);
    if (opts?.track) params.set("track", opts.track);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return fetchJSON<any[]>(`/compliance/${country}${qs}`);
  },

  getComplianceDetail: (id: string) => fetchJSON<any>(`/compliance-detail/${id}`),

  getRegulationSources: (country: string, status?: string) => {
    const params = status ? `?status=${status}` : "";
    return fetchJSON<any[]>(`/regulation-sources/${country}${params}`);
  },

  // Knowledge Base
  searchKnowledge: (query: string, opts: { scope?: string; countryCode?: string; knowledgeBaseIds?: string[]; topK?: number }) =>
    fetchJSON<{ results: any[]; count: number }>("/knowledge/search", {
      method: "POST",
      body: JSON.stringify({
        query,
        scope: opts.scope,
        country_code: opts.countryCode,
        knowledge_base_ids: opts.knowledgeBaseIds,
        top_k: opts.topK ?? 5,
      }),
    }),

  // Feedback
  submitFeedback: (data: { entity_type: string; entity_id: string; rating: "up" | "down"; comment?: string; context?: Record<string, string> }) =>
    fetchJSON<{ ok: boolean }>("/feedback", { method: "POST", body: JSON.stringify(data) }),

  getMyFeedback: () =>
    fetchJSON<Array<{ entity_type: string; entity_id: string; rating: string; comment: string | null }>>("/feedback/my"),

  // Cloud Drive
  // Guided Tasks
  getGuidedTasks: (projectId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/tasks`),

  // License Status + Submissions
  updateLicenseStatus: (projectId: string, licenseTypeId: string, data: {
    status: string; submitted_date?: string; decision_date?: string;
    expiry_date?: string; license_number?: string; notes?: string;
  }) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/licenses/${licenseTypeId}/status`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  getLicenseSubmissions: (projectId: string, licenseTypeId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/licenses/${licenseTypeId}/submissions`),

  addLicenseSubmission: (projectId: string, licenseTypeId: string, data: {
    event_type: string; event_date: string; reference_number?: string;
    regulator_contact?: string; channel?: string; description?: string;
    follow_up_date?: string;
  }) =>
    fetchJSON<{ id: string; ok: boolean }>(`/projects/${projectId}/licenses/${licenseTypeId}/submissions`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getCloudStatus: (projectId: string) =>
    fetchJSON<any>(`/projects/${projectId}/cloud/status`),

  startCloudConnect: (projectId: string, provider: string) =>
    fetchJSON<{ authUrl: string }>(`/projects/${projectId}/cloud/connect`, {
      method: "POST",
      body: JSON.stringify({ provider }),
    }),

  disconnectCloud: (projectId: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/cloud/disconnect`, { method: "POST" }),

  triggerCloudSync: (projectId: string) =>
    fetchJSON<any>(`/projects/${projectId}/cloud/sync`, { method: "POST" }),

  getCloudFolders: (projectId: string, parentId?: string) => {
    const params = parentId ? `?parent=${parentId}` : "";
    return fetchJSON<{ folders: any[]; parentId: string; parentName: string }>(`/projects/${projectId}/cloud/folders${params}`);
  },

  selectCloudFolder: (projectId: string, folderId: string, folderName: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/cloud/select-folder`, {
      method: "POST",
      body: JSON.stringify({ folderId, folderName }),
    }),

  getCloudReviewQueue: (projectId: string) =>
    fetchJSON<any[]>(`/projects/${projectId}/cloud/review`),

  resolveCloudReview: (projectId: string, reviewId: string, action: "approve" | "dismiss", docType?: string) =>
    fetchJSON<{ ok: boolean }>(`/projects/${projectId}/cloud/review/${reviewId}`, {
      method: "PATCH",
      body: JSON.stringify({ action, doc_type: docType }),
    }),

  getFeedbackSummary: () =>
    fetchJSON<{ feedback: any[]; interview_count: number; interviews_by_country: any[] }>("/admin/feedback-summary"),

  getAdminKpis: (passphrase: string) => {
    const token = getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Admin-Passphrase": passphrase,
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${BASE}/admin/kpis`, { headers }).then(res => {
      if (!res.ok) throw new Error(res.status === 403 ? "Invalid passphrase" : `API error: ${res.status}`);
      return res.json();
    });
  },
};

// Public API (no auth required)
export interface CheckupSnapshot {
  country: string;
  capacity_kw: number;
  intent: string;
  fields: Record<string, string>;
  mapped_fields: Record<string, string>;
}

export const checkupSnapshot = {
  get: (token?: string) => fetchJSON<{ snapshot: CheckupSnapshot | null }>(
    `/checkup-snapshot${token ? `?token=${token}` : ""}`
  ),
  clear: (token?: string) => fetchJSON<{ ok: boolean }>(
    `/checkup-snapshot${token ? `?token=${token}` : ""}`,
    { method: "POST" }
  ),
};

export async function getCheckup(data: {
  country_code: string;
  capacity_kw: number;
  fields: Record<string, string>;
  intent?: string;
}) {
  const res = await fetch(`${BASE}/checkup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `Checkup failed: ${res.status}`);
  }
  return res.json();
}
